<template>
    <Head title="Update News Page"/>
        <BreezeAuthenticatedLayout>
            <template #header>
                <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                    Update News Page - {{ page.title }}
                </h2>
            </template>
            <BreadCrumbs :items="navItems"></BreadCrumbs>
            <div class="w-full my-6 overflow-hidden py-12 shadow">
                <div class="px-6 pt-4 pb-2">
                    <form enctype="multipart/form-data" @submit.prevent="submit" class="w-full">
                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Title</admin-app-label>
                            <input-basic
                                placeholder="Title"
                                v-model="form.title"
                                :error="errors['title']"
                            />
                            <input-error v-if="errors['title']">
                                {{ errors.title }}
                            </input-error>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div class="mb-6 md:mb-4">
                                <admin-app-label>Excerpt</admin-app-label>
                                <input-textarea
                                    class="min-h-[200px]"
                                    placeholder="Excerpt"
                                    v-model="form.excerpt"
                                    :error="errors['excerpt']"
                                />
                                <input-error v-if="errors['excerpt']">
                                    {{ errors.excerpt }}
                                </input-error>
                                <p class="text-sm text-placeholder mt-1">
                                    Add a small excerpt that will serve as a short description of this news post.
                                    This excerpt will be used on the news landing page for a short description of the
                                    article.
                                </p>
                            </div>
                            <div class="mb-6 md:mb-4">
                                <admin-app-label>Image</admin-app-label>
                                <image-upload
                                    :key="`Image`"
                                    :label="`Image`"
                                    v-model="form.image"
                                    :error="errors['image']"
                                >
                                </image-upload>
                                <input-error v-if="errors['image']">
                                    {{ errors.image }}
                                </input-error>
                                <div v-if="page.image" class="my-4 flex">
                                    <div class="w-full md:w-1/4">
                                        <admin-app-label>Current Image</admin-app-label>
                                        <img class="object-contain p-3 border-solid border-2 border-grey-200 rounded"
                                             :src="page.image"
                                             :alt="page.title"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mb-6 md:mb-4">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <admin-app-label>CTA Text</admin-app-label>
                                    <input-basic
                                        placeholder="CTA Text"
                                        v-model="form.cta_text"
                                        :error="errors['cta_text']"
                                    />
                                    <input-error v-if="errors['cta_text']">
                                        {{ errors.cta_text }}
                                    </input-error>
                                </div>

                                <div>
                                    <admin-app-label>CTA Link</admin-app-label>
                                    <input-basic
                                        placeholder="CTA Link"
                                        v-model="form.cta_link"
                                        :error="errors['cta_link']"
                                    />
                                    <input-error v-if="errors['cta_link']">
                                        {{ errors.cta_link }}
                                    </input-error>
                                </div>
                            </div>

                            <p class="text-sm text-placeholder mt-1">
                                Add a call to action button to the bottom of this article.
                                You can link to an external site, or another page within {{ config.name }}.
                            </p>
                        </div>

                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Content</admin-app-label>
                            <input-wysiwyg
                                v-model="form.description"
                            ></input-wysiwyg>
                            <input-error v-if="errors['description']">
                                {{ errors.description }}
                            </input-error>
                        </div>

                        <div class="mb-6 md:mb-4">
                            <add-videos v-model="form.videos" :errors="errors"></add-videos>
                        </div>

                        <div class="mb-6 md:mb-4">
                            <admin-app-label>Products</admin-app-label>
                            <product-multi-select multiple
                                                  v-model="form.products"
                                                  :errors="errors"></product-multi-select>
                        </div>

                        <seo-tags v-model="form.seo_tags" :errors="errors"></seo-tags>
                        <app-button type="submit" :disabled="form.processing" class="mt-8">Update</app-button>
                    </form>
                </div>
            </div>
        </BreezeAuthenticatedLayout>
</template>

<script>
import {useForm} from "@inertiajs/inertia-vue3";
import BreezeAuthenticatedLayout from "@/Layouts/Admin/Authenticated.vue";
import {Head} from "@inertiajs/inertia-vue3";
import Label from "@/Components/Label";
import InputBasic from "@/Components/Admin/Forms/Inputs/InputBasic";
import AppButton from "@/Components/AppButton";
import InputError from "@/Components/Forms/Inputs/InputError";
import InputSelect from "@/Components/Admin/Forms/Inputs/InputSelect";
import AdminAppLabel from "@/Components/Admin/Forms/AdminAppLabel";
import ImageUpload from "@/Components/Admin/Forms/Inputs/ImageUpload";
import InputWysiwyg from "@/Components/Admin/Forms/Inputs/InputWysiwyg";
import BreadCrumbs from "@/Components/Admin/BreadCrumbs";
import ProductMultiSelect from "@/Components/Admin/Forms/ProductSelection";
import AddVideos from "@/Components/Admin/Forms/AddVideos";
import InputTextarea from "@/Components/Admin/Forms/Inputs/InputTextarea";
import SeoTags from "@/Components/Admin/Forms/SeoTags";

export default {
    name: "Edit",
    components: {
        SeoTags,
        BreadCrumbs,
        AdminAppLabel,
        AppButton,
        InputBasic,
        Label,
        BreezeAuthenticatedLayout,
        Head,
        ImageUpload,
        InputWysiwyg,
        InputSelect,
        InputError,
        ProductMultiSelect,
        AddVideos,
        InputTextarea
    },
    props: {
        name: String,
        page: Object,
        config: Object,
        errors: Object,
    },
    data() {
        return {
            navItems: [
                {title: "News", path: "/admin/news"},
            ],
            videoTypes: [
                { label: 'YouTube', id: 'youtube' },
                { label: 'Vimeo', id: 'vimeo' }
            ],
            form: this.$inertia.form({
                ...this.page,
                _method: "put",
                image: null,
                products: this.page.products,
                videos: this.page.videos,
            }),
        }
    },
    mounted() {
        this.navItems.push({
            title: this.page.slug,
            path: this.route("admin.news.edit", this.page)
        });
    },
    methods: {
        submit()
        {
            this.form.post(`/admin/news/${this.page.slug}`);
        }
    }
}
</script>

<style scoped>

</style>
